//CRN_20251218 - ⚠️ ngOnInit() called for components/directives & not services ⚠️

import { EncryptionService } from '../../general/encryption-service';
import { environment } from 'src/environments/environment';
import { eSimApiPaths } from 'src/app/core/consts';
import { ExceptionsService } from '../../general/exceptions-service';
import { HttpClientService } from '../../general/http-client-service';
import { Injectable } from '@angular/core';
import { forkJoin } from 'rxjs';
import { LocationAccuracyChangeImpactDTO } from 'src/app/core/models/system/location-awareness/location-accuracy-change-impact.dto';
import { LocationVisibilityChangeDTO } from 'src/app/core/models/system/location-awareness/location-visibility-change.dto';
import { Observable, of, switchMap } from 'rxjs';
import { StringResultDTO } from 'src/app/core/interfaces/general/string-result';
import { UserLocationAwarenessDTO } from 'src/app/core/models/system/location-awareness/user-location-awareness.dto';

/**
 * Location Awareness Service
 * Reads a user's location awareness status and drives the one-time opt-in consent link.
 */
@Injectable({ providedIn: 'root' })

export class LocationAwarenessService {

  constructor(
    private encryptionService: EncryptionService,
    private exceptionsService: ExceptionsService,
    private httpClientService: HttpClientService
  ) { }

  //RBW_20261019 - How many of a company's users would have to agree again if its default accuracy became this. Read-only:
  //                it is what the warning before saving a company is built from, so nothing is changed by asking.
  public getCompanyAccuracyChangeImpact(companyId: number, accuracyLevel: number): Observable<LocationAccuracyChangeImpactDTO> {
    try {
      return forkJoin({
               companyIdEnc    : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true),
               accuracyLevelEnc: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>accuracyLevel, true)
             })
             .pipe(
                switchMap(enc => {
                            return this.httpClientService.get<LocationAccuracyChangeImpactDTO>(environment.eSimApi +
                                                                                eSimApiPaths.LocationAwareness + "/" +
                                                                                eSimApiPaths.LocationAwarenessGetCompanyAccuracyChangeImpact +
                                                                                "?companyIdEncSS=" + enc.companyIdEnc.result +
                                                                                "&accuracyLevelEncSS=" + enc.accuracyLevelEnc.result);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getStatus(userId: number): Observable<UserLocationAwarenessDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const userIdEncSS=encResult.result;

                            return this.httpClientService.get<UserLocationAwarenessDTO>(environment.eSimApi +
                                                                                        eSimApiPaths.LocationAwareness + "/" +
                                                                                        eSimApiPaths.LocationAwarenessGetStatus +
                                                                                        "?userIdEncSS=" + userIdEncSS);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getVisibilityChangesForCompany(companyId: number, fromUtc: Date): Observable<LocationVisibilityChangeDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;

                            return this.httpClientService.get<LocationVisibilityChangeDTO[]>(
                                     environment.eSimApi
                                     + eSimApiPaths.LocationVisibilityChanges
                                     + "/"
                                     + eSimApiPaths.LocationVisibilityChangesForCompany
                                     + "?companyIdEncSS=" + companyIdEncSS
                                     + "&fromUtc=" + encodeURIComponent(fromUtc.toISOString()));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of([]);
    }
  }

  public getVisibilityChangesForSim(iccid: string, fromUtc: Date, includeCompany: boolean): Observable<LocationVisibilityChangeDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(iccid, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const iccidEncSS=encResult.result;

                            return this.httpClientService.get<LocationVisibilityChangeDTO[]>(
                                     environment.eSimApi
                                     + eSimApiPaths.LocationVisibilityChanges
                                     + "/"
                                     + eSimApiPaths.LocationVisibilityChangesForSim
                                     + "?iccidEncSS=" + iccidEncSS
                                     + "&fromUtc=" + encodeURIComponent(fromUtc.toISOString())
                                     + "&includeCompany=" + includeCompany);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of([]);
    }
  }

  public sendConsentRequest(userId: number): Observable<UserLocationAwarenessDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const userIdEncSS=encResult.result;

                            return this.httpClientService.post(environment.eSimApi +
                                                               eSimApiPaths.LocationAwareness + "/" +
                                                               eSimApiPaths.LocationAwarenessSendConsentRequest +
                                                               "?userIdEncSS=" + userIdEncSS,
                                                               ""
                                                              );
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
}
